import { RouteComponentProps } from "../../../../models/RouteComponent";
import { Typography } from "@mui/joy";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useApiService from "../../../../hooks/useApiService";

import { BasicOption, DtpInterface, RichOption } from "../../../../models/DSTI/site-dsti.interface";
import { TabPanel, TabView } from "primereact/tabview";
import { Card } from "primereact/card";
import styles from "../../site-dsti/sitedsti.module.scss";
import { formatDateTime } from "../../../../utils/DateUtils";
import TaskAndEquipment from "../../site-dsti/components/TaskAndEquipment";
import { useToast } from "../../../../context/ToastContext";
import { Button } from "primereact/button";
import ButtonPanel from "../../../../components/Shared/ButtonPanel";

function DailyTaskPlanDetail(props: RouteComponentProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [dtpDetails, setDtpDetails] = useState<DtpInterface>();
  const location = useLocation();
  const [canEdit, setCanEdit] = useState(false);

  const { loading } = useApiService<DtpInterface>({
    method: "GET",
    url: `dtp/${id}`,
    apiName: "core",
    onSuccess: data => {
      if (data) {
        setDtpDetails(data);
      }
    },
    onError: () => {
      toast("error", "Daily task plan not found", 3000);
    },
  });

  const { loading: creatingDTP, fetchData: createDTP } = useApiService<DtpInterface>({
    method: "POST",
    apiName: "core",
    url: `dtp`,
    fetchOnMount: false,
    onError: error => {
      toast("error", "Unable to create DTP, please try again later", 3000);
    },
  });

  useEffect(() => {
    if (location.pathname.includes("start")) {
      setCanEdit(true);
    }
  }, [location]);

  useEffect(() => {
    props.setGlobalLoader(loading);
  }, [loading]);

  function handleChange(
    item: RichOption | BasicOption,
    type: "task" | "equipment" | "chemical" | "vehicle"
  ) {
    if (!dtpDetails) return;

    let clonedDetails: DtpInterface = JSON.parse(JSON.stringify(dtpDetails));
    let typeObject = clonedDetails[type];

    if (!typeObject?.options) return;

    const updatedOptions = typeObject.options.map(option => {
      if (String(item.id) === String(option.id)) {
        return { ...item };
      }
      return option;
    }) as typeof typeObject.options;

    clonedDetails[type] = {
      ...typeObject,
      // @ts-ignore
      options: updatedOptions,
    };

    setDtpDetails(clonedDetails);
  }

  const handleSave = async () => {
    const res = await createDTP({
      payload: dtpDetails,
    });
    if (res && res.id && res.task) {
      toast("success", "Daily task plan successfully created", 3000);
      navigate({
        pathname: "/daily-task-plan",
      });
    }
  };

  return (
    <>
      <Typography level="h2" component="h1">
        Daily Task Plan (DTP)
      </Typography>
      <TabView>
        <TabPanel header={"Site & Project Information"}>
          <Card title={"Site Information"}>
            <div className={styles["responsive-grid-2"]}>
              <div>
                <strong>Site Name:</strong> {dtpDetails?.site_name}
              </div>
              <div>
                <strong>Date created:</strong> {formatDateTime(dtpDetails?.created_at)}
              </div>
              <div>
                <strong>DTP Responsible person:</strong> {dtpDetails?.created_by_full_name}
              </div>
            </div>
          </Card>
        </TabPanel>
        <TabPanel header={"Task & Equipment"}>
          <TaskAndEquipment details={dtpDetails} canEdit={canEdit} handleChange={handleChange} />
        </TabPanel>
      </TabView>
      {canEdit && (
        <ButtonPanel>
          <Button
            label={"Cancel"}
            outlined={true}
            color={"primary"}
            onClick={() =>
              navigate({
                pathname: "/daily-task-plan",
              })
            }
          />
          <Button
            label={"Save"}
            color={"primary"}
            disabled={creatingDTP}
            loading={creatingDTP}
            onClick={() => handleSave()}
          />
        </ButtonPanel>
      )}
    </>
  );
}

export default DailyTaskPlanDetail;
